import React, { Fragment, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    CheckboxInput,
    ButtonSecondaryCompact,
    TextLoader,
    UI_BODY_SEMIBOLD,
    UI_BODY_SEMIBOLD_SM,
    colors2,
    useScreenWidth,
    useScroll,
    DateRangePicker,
    ButtonGhostV2,
    TextInput,
    Divider,
    UI_BODY,
    makeArrayFromLength
} from "oolib";
import { FilterActionsButton } from "../../../discovery/FilterModule/comps/FilterActionsButton";
import {
    StyledActionsArea,
    StyledFiltersWrapper,
    StyledLightBoxActionButton,
    StyledOptionsLightboxWrapper,
    StyledOptionsLoaderWrapper,
    StyledOptionWrapper,
    StyledSelectClearButtonsWrapper,
    StyledSelectClearInnerWrapper,
} from "../styled";
import { useDebounce } from "@/utils/customHooks";

const LightboxBreakpointPX = 700;

const MultiSelectFilterTypes = ({ id, options, onChange, value, searchTerm, observerRef, isFetchingNextPage, isMobile }) => {

  const handleOptionClick = (option) => {
    if(option.loading) return

    let newValue = [...value];
    let isSelected = newValue.some((d) => d.value === option.value);
    if (!isSelected) {
      newValue.push(option);
    } else {
      newValue = newValue.filter((d) => d.value !== option.value);
    }

    onChange(id, newValue);
};

  const generateLoader = () => {
    return( <StyledOptionsLoaderWrapper>
                <TextLoader style={{ width: isMobile ? "2.4rem" : "1.9rem", height: isMobile ? "2.4rem" : "1.8rem", borderRadius: "4px" }}/> 
                <TextLoader style={{ width: "70%", height: isMobile ? "2.4rem" : "1.8rem" }} />
      </StyledOptionsLoaderWrapper>
    )
  }

  return (
    <Fragment>
      {options?.length ? options?.map((d, index) => (
          <StyledOptionWrapper
            key={d.value}
            onClick={() => handleOptionClick(d)}
            ref={observerRef}
          >
            {d.loading ? (
              generateLoader()
            ) : (
                <CheckboxInput className={'filterOptionsLightbox__checkboxInput'} option={d} value={value} />
            )}
          </StyledOptionWrapper>
        )) : 
          (<div style={{ padding: "1rem" }}>
            <UI_BODY>{ searchTerm ? `No results for ${searchTerm}` : `No filters`}</UI_BODY>
          </div>)
        } 
        {isFetchingNextPage && 
            makeArrayFromLength(10).map(i => 
              generateLoader()
            )
        }
    </Fragment>
  )
}

const DateRangeType = ({id, onChange, value}) => {
    const [val, setVal] = useState(value);

    const handleChange = (k, v) => {ref
      setVal(v)
      if(Array.isArray(v) && v.length === 2 && v.every(vv => !!vv === true)){
        onChange(id, v)
      }
    }

  return (
    <DateRangePicker
      onChange={handleChange}
      value={val}
      id={id}
      />
  )
}

const LightboxComp = ({id, comp, props, onChange, value, searchTerm, observerRef, isFetchingNextPage, isMobile }) => {

  const compsLookup = {
    DateRangeType,
    MultiSelectFilterTypes
  }

  const Comp = compsLookup[comp]

  return (
    <Comp
      id={id}
      onChange={onChange}
      value={value}
      searchTerm={searchTerm}
      observerRef={observerRef}
      isFetchingNextPage={isFetchingNextPage}
      isMobile={isMobile}
      {...props}
      />
  )
}

export const OptionsLightbox = ({
    ref_optionsLightboxWrapper,
    ref_sideFilterHeader,
    ref_asideFilter,
    lightboxConfig,
    onChange,
    value,
    title,
    id,
    setExpand,
    onSearchChange,
    observerRef,
    isFetchingNextPage ,
    optionHeaderRef,
    filterConfig
  }) => {

  const screenWidth = useScreenWidth();
  const actionsAreaRef = useRef();

  const [ search, setSearch ] = useState("");
  const [ filteredOptions, setFilteredOptions ] = useState(null);

  const debounce = useDebounce();
  
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    if(filterConfig?.source?.filterType === "tplType"){
      // Search through options and match with searchTerm against option.display
      const searchedFilteredOptions = value 
        ? lightboxConfig.props.options.filter(option => 
            option.display.toLowerCase().includes(value.toLowerCase())
          )
        : lightboxConfig.props.options;
      
      // Update the filtered options state
      setFilteredOptions(searchedFilteredOptions);
    } else {
      debounce(() => {
        onSearchChange && onSearchChange(value);
      });
    }
  }, [search, filterConfig, lightboxConfig, onSearchChange, debounce]);

  
  const getBoundsOfOptionsHeader = () => optionHeaderRef?.current?.getBoundingClientRect();


  useScroll(() => {
    setOptionsHeaderPos(getBoundsOfOptionsHeader())
    getDimensions()
  });

  const [ optionsHeaderPos, setOptionsHeaderPos ] = useState(getBoundsOfOptionsHeader());

  const isMobile = screenWidth < LightboxBreakpointPX;
  
  const overflowSetting = {
    StyledOptionsLightboxWrapper: lightboxConfig.comp === 'DateRangeType' ? 'visible' : 'hidden',
    StyledFiltersWrapper: lightboxConfig.comp === 'DateRangeType' ? 'visible' : 'auto'
  }

  const getDimensions = useCallback(() => {
    const viewPortHeight = window.innerHeight;
    const centerOfScreen = viewPortHeight / 2;
    const topSpace = 25; 
    const bottomSpace = 25;
    
    // Check if element is above the viewport (scrolled past top)
    if (optionsHeaderPos.bottom < 0) {
      // Element is above viewport, position at top with padding
      // Ensure we leave space at both top and bottom
      return {
        maxHeight: viewPortHeight - (topSpace + bottomSpace),
        top: topSpace,
        bottom: "auto"
      };
    }
    
    // Check if element is below the viewport (scrolled past bottom)
    if (optionsHeaderPos.top > viewPortHeight) {
      // Element is below viewport, position at bottom with padding
      // Ensure we leave space at both top and bottom
      return {
        maxHeight: viewPortHeight - (topSpace + bottomSpace),
        top: "auto",
        bottom: bottomSpace
      };
    }
    
    // Element is visible on screen - follow original logic
    if (optionsHeaderPos.top < centerOfScreen) {
      // Element is in top half of screen
      // Ensure we leave space at both top and bottom
      const height = viewPortHeight - bottomSpace - Math.max(optionsHeaderPos.top, topSpace);
      
      return {
        maxHeight: height,
        top: Math.max(optionsHeaderPos.top, topSpace),
        bottom: "auto"
      };
    } else {
      // Element is in bottom half of screen
      // Ensure we leave space at both top and bottom
      return {
        maxHeight: optionsHeaderPos.top - topSpace,
        top: "auto",
        bottom: Math.max(viewPortHeight - optionsHeaderPos.bottom, bottomSpace)
      };
    }
  }, [optionsHeaderPos]);

  const desktopFiltersWrapperHeight = getDimensions()?.maxHeight - (actionsAreaRef.current?.getBoundingClientRect().height || 0);
  const mobileFiltersWrapperHeight = isMobile && window.innerHeight - (actionsAreaRef.current?.getBoundingClientRect().height || 0);
  const optionsLightboxWidth = isMobile ? 300 : lightboxConfig.comp === 'DateRangeType' ? 300 : 250;

  // Prepare props for LightboxComp
  const lightboxProps = {...lightboxConfig.props};

  // If we have filtered options and it's a tplType filter, use the filtered options
  if (filterConfig?.source?.filterType === "tplType" && filteredOptions !== null) {
    lightboxProps.options = filteredOptions;
  }

  return createPortal(
      <StyledOptionsLightboxWrapper
            style={{ 
              overflow: overflowSetting.StyledOptionsLightboxWrapper,
              left: isMobile ? "100dvw" : ref_asideFilter.current.offsetWidth + 4,
              height: isMobile && "100svh",
              width: optionsLightboxWidth + 'px',
              ... !isMobile && { ...getDimensions() },
              }
            }
            ref={ref_optionsLightboxWrapper}
            isMobile={isMobile}
      >

      {/* Actions area, Search, sort, clear, select all */}
      <StyledActionsArea ref={actionsAreaRef}>
          {/* Title area mobile */}
              {isMobile &&
                <div style={{borderBottom:  lightboxConfig.comp === 'DateRangeType' ? `1px solid ${colors2.grey10}` : null ,}}>
                  <div style={{ padding: '1.2rem 1.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                    <UI_BODY_SEMIBOLD>{title}</UI_BODY_SEMIBOLD>
                    <ButtonSecondaryCompact onClick={() => setExpand(false)} value={"Close"}/>
                  </div>
                </div>
          }
            {!isMobile && <UI_BODY_SEMIBOLD_SM
              style={{
                color: colors2.grey80,
                padding: "0.9rem 1rem",
                // borderBottom: `1px solid ${colors2.grey10}`,
              }}
            >
              {title}
            </UI_BODY_SEMIBOLD_SM>}
            {lightboxConfig.comp === 'DateRangeType' ? null : <TextInput 
              value={search}
              S
              onChange={(id, v) => handleSearchChange(v)}
              placeholder={"Search"}
              icon="MagnifyingGlass"
              style={{ background: "yellow" }}
              />}
            {lightboxConfig?.comp === 'MultiSelectFilterTypes' && <StyledSelectClearButtonsWrapper>
              <StyledSelectClearInnerWrapper>
                <StyledLightBoxActionButton
                  disabled={lightboxConfig.props?.options?.some(i => i.loading) || lightboxConfig.props?.options?.length === value.length}
                  icon="CheckCircle"
                  iconSize={16}
                  iconWeight="bold"
                  iconColor={colors2.grey80}
                  onClick={() => onChange(id, lightboxConfig.props?.options)}
                  responsive={true}
                  isMobile={isMobile}
                >
                  Select All
                </StyledLightBoxActionButton>
                <StyledLightBoxActionButton
                  disabled={value.length === 0}
                  icon="XCircle"
                  iconSize={16}
                  iconWeight="bold"
                  iconColor={colors2.grey80}
                  onClick={() => onChange(id, [])}
                  responsive={true}
                  isMobile={isMobile}
                >
                  Clear All
                </StyledLightBoxActionButton>
              </StyledSelectClearInnerWrapper>
            </StyledSelectClearButtonsWrapper>}
          </StyledActionsArea>


      {/* Filter Options area */}
          <StyledFiltersWrapper 
            lightboxConfig={lightboxConfig}
            style={{ 
                maxHeight: isMobile ? mobileFiltersWrapperHeight : desktopFiltersWrapperHeight,
                overflow: overflowSetting.StyledFiltersWrapper
            }}>
            <LightboxComp
              id={id}
              onChange={onChange}
              value={value}
              comp={lightboxConfig.comp}
              props={lightboxProps}
              searchTerm={search}
              observerRef={observerRef}
              isFetchingNextPage={isFetchingNextPage}
              isMobile={isMobile}
              />
          </StyledFiltersWrapper>

    </StyledOptionsLightboxWrapper>,
    document.getElementById("optionsLightbox-root")
  );
};
